import { createSlice, PayloadAction, ThunkAction } from '@reduxjs/toolkit';
import {
  QuoteWizardForm,
  QuoteWizardAnsweredForm,
  QuoteWizardAnswerInstance,
} from '../components/QuoteWizard/classes';
import { StoreState } from '.';
import { sanitizeAnswers, mergeAnswers } from './utility';
import { indexNth } from '../util/findNth';
import {
  getRecommendedTermLength,
  getRecommendedCoverageAmount,
} from './utility/policyStreamUtility';
import {
  POLICYSTREAM_API_NAMES,
  POLICYSTREAM_FORMS,
} from '../components/PolicyStream/constants';
import type { SagaArgument } from './types';
import { put, take, call, spawn, select, delay } from 'redux-saga/effects';
import Bugsnag from '@bugsnag/browser';
import { errorify } from '../util';
import { Language, UnifiedCompletedForm } from '../Typescript/classes';
import type { QuoteStatus } from '../Typescript/backend/types';
import { getAuthTokenFromLocalStorage } from '../components/Authentication';
import { submitAnswerMutation, GET_TASK_POOL } from '../queries';
import { QuoteWizardQuote } from '../components/QuoteWizard/classes';
import { PolicyStreamPlanType, PolicyStreamInsurerType } from '../gql/graphql';
import { CoreQuotePolicystreamPlanPaymentFrequencyChoices as PolicyStreamPlanPaymentFrequencyChoices } from '../gql/graphql';
import { ProgressStep } from '../components/PolicyStream/components/PolicyStreamProgressBar';
import { answerInstancesToFormState } from '../components/QuoteWizard/QuoteForms/utility';
import type { InitialValues } from '../Typescript/forms';

interface ContactInformation {
  name?: string;
  email?: string;
}

export type PolicystreamPlan = Pick<
  PolicyStreamPlanType,
  | 'id'
  | 'markdownDetails'
  | 'premiumAnnualDollars'
  | 'premiumMonthlyDollars'
  | 'premiumQuarterlyDollars'
  | 'termLength'
  | 'coverageAmountDollars'
> & {
  insurer?: null | Pick<
    PolicyStreamInsurerType,
    'id' | 'displayName' | 'logoUrl'
  >;
};

export interface StructuredPolicyStreamRatingData {
  policyLengthYears: number;
  coverageAmountDollars: number;
  // coversSpouse: boolean;
}
export interface Recommendation {
  policyLengthYears?: number;
  coverageAmountDollars?: number;
}
export const DEFAULT_POLICY_LENGTH_YEARS = 20;
export const DEFAULT_COVERAGE_AMOUNT_DOLLARS = 500_000;
// export const DEFAULT_COVERS_SPOUSE = false;

// interface MiscUserInformation {}

const PossibleErrorTypes = {
  incompleteQuoteSubmission: 'incompleteQuoteSubmission',
  completeQuoteSubmission: 'completeQuoteSubmission',
} as const;
type PossibleError = typeof PossibleErrorTypes[keyof typeof PossibleErrorTypes];
export type PossibleErrorValues = null | Error;

export interface PolicystreamState {
  forms: Array<QuoteWizardForm>;
  formAnswers: Record<string, QuoteWizardAnsweredForm>;
  contactInformation: ContactInformation;
  requestedRecommendation: boolean;
  recommendation: Recommendation | null | undefined;
  selectedPlan: PolicystreamPlan | null;
  planPaymentFrequency: PolicyStreamPlanPaymentFrequencyChoices | null;
  structuredRatingData: StructuredPolicyStreamRatingData;
  currentQuoteUuid: null | string;
  submittedQuotes: Array<QuoteWizardQuote>;
  quoteStatus: null | QuoteStatus;
  saveButtonVisible: boolean;
  errors: { [k in PossibleError]: PossibleErrorValues };
  referralCode: string;
  // miscUserInformation: MiscUserInformation;
  dummy: string;
  progress: Array<ProgressStep>;
  quoteSubmissionLoading: boolean;
  resumeTo: string;
  quoteSubmissionTaskPoolId: null | string;
}
export const defaultInitialState: PolicystreamState = {
  forms: [],
  formAnswers: {},
  contactInformation: {},
  requestedRecommendation: false,
  recommendation: null,
  // {
  // policyLengthYears: 20,
  // coverageAmountDollars: -20,
  // },
  selectedPlan: null,
  // {
  //   id: '8',
  //   insurer: {
  //     id: '9',
  //     displayName: 'The Empire Life Insurance Company',
  //     logoUrl:
  //       'https://www.compulifeapi.com/images/logosapicanada/EMPI-large.png',
  //   },
  //   premiumAnnualDollars: '123.45',
  //   premiumMonthlyDollars: '12.34',
  //   premiumQuarterlyDollars: '',
  //   termLength: 50,
  //   coverageAmountDollars: '5000000',
  //   markdownDetails: '# Title\n\nHere are some additional details',
  // },
  planPaymentFrequency: null,
  structuredRatingData: {
    policyLengthYears: DEFAULT_POLICY_LENGTH_YEARS,
    coverageAmountDollars: DEFAULT_COVERAGE_AMOUNT_DOLLARS,
    // coversSpouse: DEFAULT_COVERS_SPOUSE,
  },
  currentQuoteUuid: null /* '7e7306b5-03e7-40f5-8e0d-43afc07f4e2b', */,
  quoteStatus: null,
  submittedQuotes: [],
  saveButtonVisible: false,
  errors: {
    completeQuoteSubmission: null,
    incompleteQuoteSubmission: null,
  },
  referralCode: '',
  dummy: 'test',
  progress: [
    { name: 'Basic Information', relevant: true, status: 'NOT_VISITED' },
    { name: 'Needs Analysis', relevant: true, status: 'NOT_VISITED' },
    { name: 'Plan Selection', relevant: true, status: 'NOT_VISITED' },
    { name: 'Application Form', relevant: true, status: 'NOT_VISITED' },
  ],
  quoteSubmissionLoading: false,
  resumeTo: '',
  quoteSubmissionTaskPoolId: null,
};

export const policystreamStore = createSlice({
  name: 'policystream',
  initialState: defaultInitialState,
  reducers: {
    /**
     * Add forms (or a form) to the state. If there's an existing one which matches it, remove it
     */
    addForms: (
      state,
      action: PayloadAction<{
        forms: QuoteWizardForm | Array<QuoteWizardForm>;
      }>,
    ) => {
      const { forms: newForms } = action.payload;
      const actualForms = Array.isArray(newForms) ? newForms : [newForms];
      const unmatchedForms = state.forms.filter(
        (existingForm) =>
          !actualForms.some(
            (newForm) => existingForm.internalName === newForm.internalName,
          ),
      );
      state.forms = [...unmatchedForms, ...actualForms];
    },
    setContactInformation: (
      state,
      action: PayloadAction<Partial<ContactInformation>>,
    ) => {
      state.contactInformation = {
        ...state.contactInformation,
        ...action.payload,
      };
    },
    setResumeTo: (state, action: PayloadAction<{ resumeTo: string }>) => {
      state.resumeTo = action.payload.resumeTo;
    },
    /**
     * Set the referral code to be sent to the backend
     */
    setReferralCode: (
      state,
      action: PayloadAction<{
        referralCode: PolicystreamState['referralCode'];
      }>,
    ) => {
      const { referralCode } = action.payload;
      if (typeof referralCode !== 'string') {
        throw new TypeError(
          'Incorrect value for referral code: it must be a string',
        );
      }
      state.referralCode = referralCode;
    },
    setSelectedPlan: (
      state,
      action: PayloadAction<PolicystreamState['selectedPlan']>,
    ) => {
      state.selectedPlan = action.payload;
    },
    updateRecommendation: (
      state,
      action: PayloadAction<Partial<Recommendation>>,
    ) => {
      state.recommendation = {
        ...state.recommendation,
        ...action.payload,
      };
    },
    /**
     * Submit answers for form and mark that one as complete
     * @param state - state
     * @param action - action. Should have payload { form: Form, answers: [Answer] }
     */
    submitAnswersForForm: (
      state,
      action: PayloadAction<{
        form: QuoteWizardForm;
        answers: Array<QuoteWizardAnswerInstance>;
        replace?: boolean; // if true, then the answers sent in will replace matching ones in state (instead of a wholesale replacement)
        nth?: number; // the instance of the answer to replace. Mostly for
      }>,
    ) => {
      let { form, answers, replace = false, nth } = action.payload;

      answers = sanitizeAnswers(answers); // get rid of the undefined ones and fix numbering

      const existingAnswers = state.formAnswers[form.id]
        ? state.formAnswers[form.id].answers
        : [];
      let mergedAnswers: Array<QuoteWizardAnswerInstance> = [];
      if (replace) {
        if (typeof nth !== 'number') {
          throw new Error(`Error with nth ${nth}`);
        }
        if (answers.length > 1) {
          throw new Error(
            `In submitAnswersForForm, when replacing can only replace a single answer at a time, not ${
              answers.length
            } - ${JSON.stringify(answers, null, 4)}`,
          );
        }
        const answer = answers[0];
        if (answer.subAnswers.length > 0) {
          answer.value = nth; // because it comes from the process with value = 1; this'll be a nested question of some sort
        }
        // do the replacement here
        const matchingIndex = indexNth(existingAnswers, nth, (elem) => {
          return answer.matchQuoteWizardQuestionInstanceById(
            elem.questionInstance,
          );
        });
        if (matchingIndex === -1) {
          // i.e. there is no matching answer -> create a new one
          mergedAnswers = [...existingAnswers, answer];
        } else {
          // replace the existing one
          mergedAnswers = [...existingAnswers];
          mergedAnswers.splice(matchingIndex, 1, answer);
        }
      } else {
        mergedAnswers = mergeAnswers(existingAnswers, answers);
      }

      // find the form matching the policy that is coming in (since the one coming in contains just a subset of the questions in the real form)
      let matchingForm = state.forms.find(
        (formInState) => formInState.internalName === form.internalName,
      );
      // fall back to the one that's sent in
      matchingForm = matchingForm ? matchingForm : form;

      state.formAnswers[form.id] = new QuoteWizardAnsweredForm({
        form: matchingForm,
        answers: mergedAnswers,
        inProgress: false,
        complete: true,
      });
    },
    /**
     * Sets the UUID of the quote currently being completed
     * @param state - the state
     * @param action - action. Should have payload { uuid: The uuid which is to be set }
     */
    setCurrentQuoteUuid: (
      state,
      action: PayloadAction<{
        uuid: string;
      }>,
    ) => {
      const { uuid } = action.payload;
      state.currentQuoteUuid = uuid;
    },
    /**
     * Once the user has finished filling out the forms, this marks all of the forms as being in progress. It also commits the blob and email to the store.
     * @param state - state
     * @param action - Action. Should have a payload { formsWithAnswers: [Form], quoteBlob: str (the base64 blob of the quote pdf), clientEmail: str, complete: Boolean (whether the quote is complete or should be saved for later) }. formsWithAnswers comes from `formAnswers` in the store, but as an array and filtered by the currently selected business (or generic, since those forms can be used for any business). Has almost the same shape as formAnswers:
     * [
     * 	{
     * 		form: Form,
     * 		answers: [Answer],
     * 		inProgress: bool,
     * 		complete: bool
     * 	 }
     * 	]
     *
     * 	See <QuoteReview /> for more details
     *
     * 	Additional attributes in the payload are used by the Saga that handles this action (`handleQuoteSubmission`)
     */
    confirmAnswersForForms: (
      state,
      action: PayloadAction<{
        formsWithAnswers: Array<UnifiedCompletedForm>;
        clientEmail?: string;
        clientName?: string;
        message?: string;
        status?: QuoteStatus;
        // currentQuoteUuid: PolicystreamState['currentQuoteUuid'];
        signature?: string | null;
      }>,
    ) => {
      const forms = action.payload.formsWithAnswers.map(({ form }) => form);

      forms.forEach((form) => {
        // we need this check because e.g. when saving an incomplete form, we generate a `fake` form answer that doesn't exist in state
        if (state.formAnswers[form.id]) {
          state.formAnswers[form.id].inProgress = true;
        }
      });
    },
    /**
     * Marks a quote (set of forms) as submitted by marking all of the `inProgress` flags as false, setting the `completedQuote` property of all of the formAnswers to the current quote, and adds the quote to the `submittedQuotes` state attribute
     * Called through the Saga that deals with the `confirmAnswersForForms` action
     * @param state - State
     * @param action - Action. Should have a payload of {
     *    formsWithsAnswers: Same as for confirmAnswersForForms reducer
     *    quote: Quote
     *    businessType: BusinessLine
     * }
     */
    markQuoteAsSubmitted: (
      state,
      action: PayloadAction<{
        formsWithAnswers: Array<QuoteWizardAnsweredForm>;
        quote: QuoteWizardQuote;
      }>,
    ) => {
      //console.log('Beginning markQuoteAsSubmitted reducer');
      const forms = action.payload.formsWithAnswers.map(({ form }) => form);

      forms.forEach((form) => {
        if (state.formAnswers[form.id]) {
          // we need this check because when saving an incomplete form, sometimes we generate a `fake` form answer that doesn't exist in state
          state.formAnswers[form.id].inProgress = false;
          state.formAnswers[form.id].completedQuote = action.payload.quote;
        }
      });

      state.submittedQuotes.push(action.payload.quote);
      //console.log('Finished markQuoteAsSubmitted reducer');
    },
    /**
     * Updates the errors part of the store
     * @param state - state
     * @param action - Action. Payload should be { type: Error | null, type: Error | null, ... }. e.g. { completeQuoteSubmission: null, incompleteQuoteSubmission: new Error('Bang!') }. null -> a cleared / handled error
     */
    updateErrors: (
      state,
      action: PayloadAction<
        Partial<Record<PossibleError, PossibleErrorValues>>
      >,
    ) => {
      state.errors = {
        ...state.errors,
        ...action.payload,
      };
    },
    /**
     * Updates whether the quote submission is loading or not
     * @param state
     * @param _action
     */
    setQuoteSubmissionLoading: (
      state,
      action: PayloadAction<{ loading: boolean }>,
    ) => {
      state.quoteSubmissionLoading = action.payload.loading;
    },
    /**
     * Resets the state
     */
    // @ts-expect-error
    resetState: (state, _action: PayloadAction<undefined>) => {
      state = { ...defaultInitialState };
    },
    setSaveButtonVisibility: (state, action: PayloadAction<boolean>) => {
      state.saveButtonVisible = action.payload;
    },
    setPlanPaymentFrequency: (
      state,
      action: PayloadAction<PolicystreamState['planPaymentFrequency']>,
    ) => {
      state.planPaymentFrequency = action.payload;
    },
    setDummy: (state, action: PayloadAction<string>) => {
      state.dummy = action.payload;
    },
    setRequestedRecommendation: (state, action: PayloadAction<boolean>) => {
      state.requestedRecommendation = action.payload;
    },
    setStepStatus: (
      state,
      action: PayloadAction<{
        name: ProgressStep['name'];
        status: ProgressStep['status'];
      }>,
    ) => {
      const { name, status } = action.payload;
      state.progress = state.progress.map((existingStep) => {
        if (existingStep.name === name) {
          return { ...existingStep, status };
        }
        return existingStep;
      });
    },
    setStepRelevance: (
      state,
      action: PayloadAction<{
        name: ProgressStep['name'];
        relevant: ProgressStep['relevant'];
      }>,
    ) => {
      const { name, relevant } = action.payload;
      state.progress = state.progress.map((existingStep) => {
        if (existingStep.name === name) {
          return { ...existingStep, relevant };
        }
        return existingStep;
      });
    },
    /**
     * Set the quote submission task pool id
     */
    setQuoteSubmissionTaskPoolId: (
      state,
      action: PayloadAction<PolicystreamState['quoteSubmissionTaskPoolId']>,
    ) => {
      state.quoteSubmissionTaskPoolId = action.payload;
    },
  },
});

export const getSelectedPlan = (state: StoreState) =>
  state.policystreamStore.selectedPlan;
export const getPolicyStreamPolicyLengthYears = (state: StoreState) => {
  return state.policystreamStore.structuredRatingData.policyLengthYears;
};
export const getPolicyStreamCoverageAmountDollars = (state: StoreState) => {
  return state.policystreamStore.structuredRatingData.coverageAmountDollars;
};

export const getResumeTo = (state: StoreState) => {
  return state.policystreamStore.resumeTo;
};

// export const getPolicyStreamCoversSpouse = (state: StoreState) => {
//   return state.policystreamStore.structuredRatingData.coversSpouse;
// };
export const generateGetAnsweredForm = ({
  id,
  internalName,
}: {
  id?: QuoteWizardAnsweredForm['form']['id'];
  internalName?: QuoteWizardForm['form']['internalName'];
}) => {
  return (state: StoreState) => {
    if (id) {
      return state.policystreamStore.formAnswers[id] || null;
    } else if (internalName) {
      const found = Object.values(state.policystreamStore.formAnswers).find(
        (form) => form.form.internalName === internalName,
      );
      if (found) {
        return found;
      }
    }
    return null;
  };
};

/**
 * Takes a wizard `form` and returns a selector that reads its answers
 * (`QuoteWizardAnsweredForm`) from `policystreamStore.formAnswers`, or `null`.
 */
export const generateGetPolicyStreamQuizFormAnsweredForm = (
  form: Pick<QuoteWizardForm, 'id'>,
) => {
  return generateGetAnsweredForm({ id: form.id });
};

/**
 * Builds informed `initialValues` for a Policy Stream quiz step from an answered
 * form already in the store (same shape as {@link generateGetPolicyStreamQuizFormInitialValues}).
 */
export const policyStreamQuizFormInitialValuesFromAnsweredForm = (
  form: Pick<QuoteWizardForm, 'id' | 'questionInstances'>,
  answeredForm: QuoteWizardAnsweredForm | null,
): InitialValues | null => {
  if (!form.id || !answeredForm) {
    return null;
  }
  const initialValues: InitialValues = answerInstancesToFormState(
    answeredForm.answers,
    form.questionInstances,
  );
  return initialValues;
};

/**
 * Selects informed `initialValues` for a quiz `form` from `policystreamStore.formAnswers`.
 */
export const generateGetPolicyStreamQuizFormInitialValues = (
  form: Pick<QuoteWizardForm, 'id' | 'questionInstances'>,
) => {
  const selectAnsweredForm = generateGetPolicyStreamQuizFormAnsweredForm(form);
  return (state: StoreState): InitialValues | null => {
    return policyStreamQuizFormInitialValuesFromAnsweredForm(
      form,
      selectAnsweredForm(state),
    );
  };
};

export const getAnsweredForms = (state: StoreState) => {
  return Object.values(state.policystreamStore.formAnswers);
};
export const getAllForms = (state: StoreState) => {
  return state.policystreamStore.forms;
};
export const getCurrentQuoteUuid = (state: StoreState) => {
  return state.policystreamStore.currentQuoteUuid;
};

export const getQuoteSubmissionTaskPoolId = (state: StoreState) => {
  return state.policystreamStore.quoteSubmissionTaskPoolId;
};

export const getQuoteStatus = (state: StoreState) => {
  return state.policystreamStore.quoteStatus;
};
export const getErrors = (state: StoreState) => {
  return state.policystreamStore.errors;
};
export const getIncompleteQuoteSubmissionError = (state: StoreState) => {
  return state.policystreamStore.errors.incompleteQuoteSubmission;
};

export const getQuoteSubmissionLoading = (state: StoreState) => {
  return state.policystreamStore.quoteSubmissionLoading;
};

export const getRecommendation = (state: StoreState) => {
  return state.policystreamStore.recommendation;
};

export const getLastSubmittedQuote = (state: StoreState) => {
  return state.policystreamStore.submittedQuotes.length > 0
    ? state.policystreamStore.submittedQuotes[
        state.policystreamStore.submittedQuotes.length - 1
      ]
    : null;
};

export const getSex = (state: StoreState): string | null => {
  let sex: string | null = null;
  const basicInfoAnsweredForm = Object.values(
    state.policystreamStore.formAnswers,
  ).find((form) => form.form.internalName === POLICYSTREAM_FORMS.basicInfo);
  if (basicInfoAnsweredForm) {
    sex = basicInfoAnsweredForm.getAnswerValueByApiName(
      POLICYSTREAM_API_NAMES.basicInfo.sex,
    );
  }

  if (!sex) {
    const carrierAnsweredForm = Object.values(
      state.policystreamStore.formAnswers,
    ).find(
      (form) => form.form.internalName === POLICYSTREAM_FORMS.applicationForm,
    );
    if (carrierAnsweredForm) {
      sex = carrierAnsweredForm.getAnswerValueByApiName(
        POLICYSTREAM_API_NAMES.applicationGeneral.sex,
      );
    }
  }
  return sex;
};
/**
 * Returns the birthdate in YYYY-MM-DD format (or none if it can't find / parse it)
 */
export const getBirthDate = (state: StoreState): null | string => {
  const basicInfoAnsweredForm = Object.values(
    state.policystreamStore.formAnswers,
  ).find((form) => form.form.internalName === POLICYSTREAM_FORMS.basicInfo);
  const birthDayRaw = basicInfoAnsweredForm
    ? basicInfoAnsweredForm.getAnswerValueByApiName(
        POLICYSTREAM_API_NAMES.basicInfo.dateOfBirth,
      )
    : null;
  if (!birthDayRaw) {
    return null;
  }
  const split = birthDayRaw.split('/');
  if (split.length !== 3) {
    return null;
  }
  const [monthRaw, dayRaw, yearRaw] = split;
  return `${yearRaw}-${monthRaw}-${dayRaw}`;
};

export const getIsSmoker = (state: StoreState): null | boolean => {
  const basicInfoAnsweredForm = Object.values(
    state.policystreamStore.formAnswers,
  ).find((form) => form.form.internalName === POLICYSTREAM_FORMS.basicInfo);
  const isSmoker = basicInfoAnsweredForm
    ? basicInfoAnsweredForm.getAnswerValueByApiName(
        POLICYSTREAM_API_NAMES.basicInfo.nicotineUse,
      )
    : null;
  return isSmoker === 'yes' ? true : isSmoker === 'no' ? false : null;
};
/**
 * Gets the Compulife code that the user selected (maps to a province) - e.g. AB -> "1", BC -> "2", &c.
 */
export const getProvinceCode = (state: StoreState): string | null => {
  const basicInfoAnsweredForm = Object.values(
    state.policystreamStore.formAnswers,
  ).find((form) => form.form.internalName === POLICYSTREAM_FORMS.basicInfo);
  const provinceCode = basicInfoAnsweredForm
    ? basicInfoAnsweredForm.getAnswerValueByApiName(
        POLICYSTREAM_API_NAMES.basicInfo.province,
      )
    : null;
  return provinceCode || null;
};

interface GenerateGetAnswerValueByApiNameOptions {
  internalName?: string;
}
export const generateGetAnswerValueByApiName = (
  apiName: string,
  options: GenerateGetAnswerValueByApiNameOptions = {},
) => {
  return (state: StoreState) => {
    let relevantAnsweredForms = Object.values(
      state.policystreamStore.formAnswers,
    );
    if (options.internalName) {
      relevantAnsweredForms = relevantAnsweredForms.filter(
        (form) => form.internalName === options.internalName,
      );
    }
    for (let answeredForm of relevantAnsweredForms) {
      const matching = answeredForm.getAnswerValueByApiName(apiName);
      if (matching) {
        return matching;
      }
    }
    return undefined;
  };
};

const generateIsFormAnsweredFunction = (
  state: StoreState,
  formInternalName: string,
) => {
  const form = Object.values(state.policystreamStore.formAnswers).find(
    (form) => form.form.internalName === formInternalName,
  );
  return form ? form.complete : false;
};

export const isBasicInfoFormAnswered = (state: StoreState) =>
  generateIsFormAnsweredFunction(state, POLICYSTREAM_FORMS.basicInfo);

export const isBasicClientFormAnswered = (state: StoreState) =>
  generateIsFormAnsweredFunction(state, POLICYSTREAM_FORMS.basicClientInfo);

export const isGeneralInformationFormAnswered = (state: StoreState) =>
  generateIsFormAnsweredFunction(state, POLICYSTREAM_FORMS.applicationForm);

export const isRecommendationFormAnswered = (state: StoreState) =>
  generateIsFormAnsweredFunction(state, POLICYSTREAM_FORMS.needsAnalysis);

export const generateGetFormByInternalName = (internalName: string) => {
  return (state: StoreState) => {
    return state.policystreamStore.forms.find(
      (form) => form.internalName === internalName,
    );
  };
};

export const getSaveButtonVisibility = (state: StoreState) => {
  return state.policystreamStore.saveButtonVisible;
};

export const getPlanPaymentFrequency = (state: StoreState) => {
  const frequency = state.policystreamStore.planPaymentFrequency;
  return frequency;
};

export const getDummy = (state: StoreState) => {
  return state.policystreamStore.dummy;
};

export const getProgressSteps = (state: StoreState) => {
  return state.policystreamStore.progress;
};

const getReferralCode = (state: StoreState) => {
  return state.policystreamStore.referralCode;
};
/* ----- Thunks -------- */

export const generateSubmitPolicyStreamFormAnswersThunk = ({
  form,
  answers,
}: {
  form: QuoteWizardForm;
  answers: Array<QuoteWizardAnswerInstance>;
}): ThunkAction<void, StoreState, unknown, any> => {
  return (dispatch, getState) => {
    const state = getState();
    dispatch(policystreamStore.actions.submitAnswersForForm({ form, answers }));

    const isNeedsAnalysisForm =
      form.internalName === POLICYSTREAM_FORMS.needsAnalysis;
    const isBasicInfoForm = form.internalName === POLICYSTREAM_FORMS.basicInfo;
    // if it's a recommendation form, then they requested a recommendation
    if (isNeedsAnalysisForm) {
      dispatch(policystreamStore.actions.setRequestedRecommendation(true));
    }
    const requestedRecommendation = isNeedsAnalysisForm
      ? true
      : state.policystreamStore.requestedRecommendation;

    // if they requested a recommendation and this is a recommendation form (or basic info - it has age), try to update the recommendations based on the current answers
    if (requestedRecommendation && (isNeedsAnalysisForm || isBasicInfoForm)) {
      const recommendedCoverageAmount =
        getRecommendedCoverageAmount(getState()) ||
        DEFAULT_COVERAGE_AMOUNT_DOLLARS;
      const recommendedTermLength =
        getRecommendedTermLength(getState()) || DEFAULT_POLICY_LENGTH_YEARS;
      dispatch(
        policystreamStore.actions.updateRecommendation({
          coverageAmountDollars: recommendedCoverageAmount,
          policyLengthYears: recommendedTermLength,
        }),
      );
    }
  };
};

/* --------------- Sagas ------------- */
function* handleQuoteSubmission({ client }: SagaArgument) {
  while (true) {
    const {
      payload: {
        formsWithAnswers,
        clientName,
        clientEmail,
        message,
        status = 'COMPLETE',
        signature,
      },
    } = yield take(policystreamStore.actions.confirmAnswersForForms.type);

    const completedFormInputs = formsWithAnswers.map(
      (form: UnifiedCompletedForm) => {
        return form.toCompletedFormInput();
      },
    );
    // @ts-expect-error
    const resumeTo = yield select(getResumeTo);
    const selectedLanguage = Language.english;
    const token = getAuthTokenFromLocalStorage();
    // @ts-expect-error
    const currentQuoteUuid = yield select(getCurrentQuoteUuid);
    // @ts-expect-error
    const selectedPlan = yield select(getSelectedPlan);
    let recommendation;
    // @ts-expect-error
    const rawRecommendation = yield select(getRecommendation);
    // @ts-expect-error
    const referralCode = yield select(getReferralCode);
    if (rawRecommendation) {
      let { __typename, ...gatheredRecommendation } = rawRecommendation;
      recommendation = gatheredRecommendation;
    }
    // @ts-expect-error
    const policystreamPlanPaymentFrequency = yield select(
      getPlanPaymentFrequency,
    );

    try {
      const newRecommendation = {
        policyLengthYears: recommendation?.policyLengthYears,
        coverageAmountDollars: recommendation?.coverageAmountDollars.toString(),
      };

      const { data, error } = yield call(client.mutate, {
        mutation: submitAnswerMutation,
        variables: {
          languageCode: selectedLanguage.shortName,
          completedFormInputs,
          clientName,
          clientEmail,
          message,
          referralCode,
          status,
          currentQuoteUUID: currentQuoteUuid,
          renewalInProgress: false,
          isPolicystreamApplication: true,
          policystreamPlanId: selectedPlan ? selectedPlan.id : undefined,
          policyStreamRecommendation: newRecommendation,
          policystreamPlanPaymentFrequency,
          resumeTo,
          signature,
          token,
        },
        // TODO: Re-enable cache updating later
        // update: (cache, { data }) => {
        //   let quotes;

        //   //console.log(data);

        //   try {
        //     quotes = cache.readQuery({
        //       query: RELAY_QUOTES,
        //       // @ts-expect-error
        //       variables: { token },
        //     });
        //   } catch (e) {
        //     console.error('Failed to read quote list from cache', e);
        //   }

        //   try {
        //     cache.writeQuery({
        //       query: RELAY_QUOTES,
        //       // @ts-expect-error
        //       variables: { token },
        //       data: {
        //         // @ts-expect-error
        //         relayQuotes: {
        //           // @ts-expect-error
        //           edges: [...quotes.relayQuotes.edges, data.createQuote.quote],
        //         },
        //       },
        //     });
        //   } catch (e) {
        //     //console.log('Failed to write to quote list in cache');
        //   }
        // },
      });
      //console.log('Done calling mutation');

      if (error) {
        Bugsnag.notify(error);
        console.error(error);
      }

      if (error) {
        console.error(error);
        Bugsnag.notify(error);
      }

      // Extract task pool id from the response
      const taskPoolId = data?.createQuote?.taskPool?.id ?? null;

      if (taskPoolId) {
        yield put(
          policystreamStore.actions.setQuoteSubmissionTaskPoolId(taskPoolId),
        );
      }

      // TODO: The create_quote mutation response structure has changed
      // The following code may need to be updated to work with the new response structure
      // const isOk = data && data.createQuote; // Note: response structure changed, may not have 'ok' field

      // if (isOk && taskPoolId) {
      // TODO: Update this section once the new response structure is fully understood
      // if (storeExistingQuoteUuidOnComplete) {
      //   try {
      //     window.localStorage.setItem(
      //       'existingQuoteUuid',
      //       data.createQuote.quote.uniqueID,
      //     );
      //   } catch (e) {
      //     console.error(e);
      //     Bugsnag.notify(errorify(e));
      //   }
      // }

      // yield put(
      //   policystreamStore.actions.markQuoteAsSubmitted({
      //     formsWithAnswers,
      //     quote: data.createQuote.quote,
      //   }),
      // );
      // yield put(
      //   policystreamStore.actions.setCurrentQuoteUuid({
      //     uuid: data.createQuote.quote.uniqueID,
      //   }),
      // );

      // sort out the analytics
      //console.log('About to dispatch application-related thunk');
      //switch (status) {
      //  case 'COMPLETE':
      //    //console.log('About to dispatch createApplicationFinishedThunk');
      //    // @ts-expect-error
      //    yield put(createApplicationFinishedThunk());
      //    break;
      //  case 'INCOMPLETE':
      //    //console.log('About to dispatch createApplicationSavedThunk');
      //    // @ts-expect-error
      //    yield put(createApplicationSavedThunk());
      //    break;
      //  // case 'CONTACT_INFO_ONLY':
      //  //   // @ts-expect-error
      //  //   yield put(createApplicationBegunThunk());
      //  //   break;
      //}
      //} else {
      //console.error(data);
      //Bugsnag.notify(new Error(JSON.stringify(data)));
      //if (['COMPLETE', 'RENEWED'].includes(status)) {
      //  yield put(
      //    policystreamStore.actions.updateErrors({
      //      completeQuoteSubmission: data || 'Unknown error',
      //    }),
      //  );
      //} else {
      //  yield put(
      //    policystreamStore.actions.updateErrors({
      //      incompleteQuoteSubmission: data || 'Unknown error',
      //    }),
      //  );
      //}
      //}
    } catch (error) {
      console.error(error);
      Bugsnag.notify(errorify(error));
      if (['COMPLETE', 'RENEWED'].includes(status)) {
        yield put(
          policystreamStore.actions.updateErrors({
            completeQuoteSubmission: errorify(error),
          }),
        );
      } else {
        yield put(
          policystreamStore.actions.updateErrors({
            incompleteQuoteSubmission: errorify(error),
          }),
        );
      }
    }
  }
}

function* pollTaskPoolForQuoteUuid({ client }: SagaArgument) {
  const POLLING_INTERVAL_MS = 1000; // 1 second as requested

  while (true) {
    // Wait for task pool id to be set
    yield take(policystreamStore.actions.setQuoteSubmissionTaskPoolId.type);

    // @ts-expect-error
    let taskPoolId = yield select(getQuoteSubmissionTaskPoolId);

    // Only poll if we have a task pool id
    if (!taskPoolId) {
      continue;
    }

    // Poll until we find quote_uuid or task pool id is cleared
    while (taskPoolId) {
      try {
        // Query the task pool
        const { data, error } = yield call(client.query, {
          query: GET_TASK_POOL,
          variables: { taskPoolId },
          fetchPolicy: 'network-only', // Always fetch fresh data
        });

        if (error) {
          console.error('Error polling task pool:', error);
          Bugsnag.notify(errorify(error));
          break;
        }

        const taskPool = data?.getTaskPool;
        if (taskPool?.additionalInformation) {
          try {
            const additionalInfo = JSON.parse(taskPool.additionalInformation);

            if (additionalInfo && typeof additionalInfo === 'object') {
              const quoteUuid = additionalInfo?.quote_uuid;

              // If we found a non-empty quote_uuid, set it and stop polling
              if (
                quoteUuid &&
                typeof quoteUuid === 'string' &&
                quoteUuid.trim() !== ''
              ) {
                yield put(
                  policystreamStore.actions.setCurrentQuoteUuid({
                    uuid: quoteUuid,
                  }),
                );
                // Stop polling but don't clear the task pool id - hooks may be listening for it
                break;
              }
            }
          } catch (parseError) {
            // Invalid JSON, continue polling
            console.error('Error parsing additionalInformation:', parseError);
          }
        }

        // Check if task pool id was cleared (e.g., by another process)
        // @ts-expect-error
        taskPoolId = yield select(getQuoteSubmissionTaskPoolId);

        if (!taskPoolId) {
          break;
        }

        // Wait before next poll
        yield delay(POLLING_INTERVAL_MS);
      } catch (error) {
        console.error('Error in polling saga:', error);
        Bugsnag.notify(errorify(error));
        break;
      }
    }
  }
}

export function* PolicyStreamStoreSaga({ client }: SagaArgument) {
  yield spawn(handleQuoteSubmission, { client });
  yield spawn(pollTaskPoolForQuoteUuid, { client });
}

export const {
  setSelectedPlan,
  setPlanPaymentFrequency,
  setResumeTo,
  setReferralCode,
  setQuoteSubmissionTaskPoolId,
  markQuoteAsSubmitted,
  setCurrentQuoteUuid,
} = policystreamStore.actions;
